import { Css } from "@homebound/beam";
import { Price } from "src/components/Price";
import { ImpactCard } from "src/routes/projects/change-events/guided/components/ImpactCard";
import { celisImpactTotals } from "src/routes/projects/change-events/guided/utils";
import { ReviewImpact_ChangeEventFragment } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/2.ReviewImpactStep.generated";
import { pluralize } from "src/utils";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";

type BudgetAndScopeImpactCardsProps = {
  changeEvent: ReviewImpact_ChangeEventFragment;
  /** How many items the project has today, i.e. before this change event is applied. */
  currentProjectItemCount: number;
  /** True when the change event charges the client, i.e. it has a price impact to show. */
  isChargeable: boolean;
};

/** The three "what does this swap do to the project" cards at the top of the Review Impact step. */
export function BudgetAndScopeImpactCards(props: BudgetAndScopeImpactCardsProps) {
  const { changeEvent, currentProjectItemCount, isChargeable } = props;
  const { lineItems, totalCostInCents, totalPriceInCents, budgetImpactInCents, project } = changeEvent;

  const { totalAddedInCents, totalRemovedInCents, addedItems, removedItems, netItems, changedItems } =
    celisImpactTotals(lineItems);
  const currentBudgetInCents = project.budgetFinancials.currentBudgetInCents;
  const changedItemsLabel = `${changedItems} ${pluralize(changedItems, "item")} changed`;

  return (
    <div css={Css.dg.gtc("repeat(2, 1fr)").gap3.$}>
      <ImpactCard
        icon="invoiceDollar"
        title="Budget Impact"
        headline={<ReviewImpactedPrice valueInCents={budgetImpactInCents} />}
        rows={[
          { label: "Total Added", value: <Price valueInCents={totalAddedInCents} /> },
          { label: "Total Removed", value: <Price valueInCents={totalRemovedInCents} /> },
          { label: "Budget Current", value: <Price valueInCents={currentBudgetInCents} /> },
          { label: "Budget After", value: <Price valueInCents={currentBudgetInCents + totalCostInCents} /> },
          { label: "Cost Impact", value: <ReviewImpactedPrice valueInCents={totalCostInCents} /> },
          ...(isChargeable
            ? [{ label: "Price Impact", value: <ReviewImpactedPrice valueInCents={totalPriceInCents} /> }]
            : []),
        ]}
      />
      <ImpactCard
        icon="list"
        title="Scope Impact"
        headline={changedItemsLabel}
        rows={[
          { label: "Added", value: addedItems },
          { label: "Removed", value: removedItems },
          { label: "Current Items", value: currentProjectItemCount },
          { label: "Items After", value: currentProjectItemCount + netItems },
        ]}
      />
    </div>
  );
}
